/**
 * Duration formatting for session timelines.
 *
 * Two different jobs, hence two functions: `formatClock` is a fixed-width
 * elapsed-time gutter that must stay column-aligned down the timeline, while
 * `formatDwell` is prose-ish and drops precision once it stops mattering.
 */

/** Elapsed time from session start, as m:ss (or h:mm:ss past an hour). */
export function formatClock(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Time spent on one event. Sub-minute precision matters; hours don't. */
export function formatDwell(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ${Math.round((ms % 60_000) / 1000)}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/** Session length for the list column — coarser than dwell. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/** Local date+time for the "started" column, as `Jul 22, 14:22`.
 *
 *  Built from parts rather than `toLocaleString`, which renders the combined
 *  form with the locale's own joiner ("Jul 31 at 08:28 PM") and follows the
 *  locale's 12/24-hour preference — neither of which is wanted in a dense
 *  admin table. */
export function formatStarted(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
}
